import { createSelector } from 'reselect';
import { omit } from '../../../core/util/utils';
import * as actions from './search-as-you-type.actions';
import { SearchAsYouTypeModel } from './search-as-you-type.model';
import { SearchResultModel } from './search-result.model';

interface InstanceState {
    // indicator to control when the user can add new items to `value` that won't be given by the data provider service
    allowCustomValues: boolean;
    // the id currently highlighted search result
    highlighted: string;
    // the id of this instance state - this allows us to have multiple instances of this feature on one page
    id: string;
    // indicator whether to allow multiple results to be saved in `value`
    multiple: boolean;
    // the list of search results that match the term - this list is provided by the SearchAsYouTypeDataProviderService
    results: Array<SearchResultModel>;
    // the value that is entered by the user as the search term
    searchValue: string;
    // indicator if all search results should be shown
    showAllResults: boolean;
    // indicator if the search results container should be opened
    showResults: boolean;
    // the result(s) that are selected by the user as the value. If multi === false, then max length of value is 1
    value: Array<SearchResultModel>;
}

export interface State {
    instances: {[id: string]: InstanceState};
}

export const initialInstanceState: InstanceState = {
    allowCustomValues: false,
    highlighted: '',
    id: '',
    multiple: false,
    results: [],
    searchValue: '',
    showAllResults: false,
    showResults: false,
    value: []
};

export const initialState: State = {
    instances: {}
};

// tslint:disable-next-line:only-arrow-functions
export function reducer(state = initialState, action: actions.Actions): State {

    switch (true) {
        case (action.type as any) === actions.DESTROY: {
            return {
                ...state,
                instances: omit(state.instances, action.payload.instanceId)
            };
        }
        case action.type && !!action.type.match(/^\[SearchAsYouType]/): {
            return {
                ...state,
                instances: {
                    ...state.instances,
                    [action.payload.instanceId]: instanceReducer(state.instances[action.payload.instanceId], action)
                }
            };
        }
        default: {
            return state;
        }
    }
}

// tslint:disable-next-line:only-arrow-functions cyclomatic-complexity
function instanceReducer(state: InstanceState = initialInstanceState, action: actions.Actions): InstanceState {
    switch (action.type) {
        case actions.ADD_CUSTOM_VALUE: {

            if (!state.allowCustomValues) {
                return state;
            }

            const newItem: SearchResultModel = {
                id: state.searchValue,
                text: state.searchValue,
                value: state.searchValue
            };

            return {
                ...state,
                value: state.multiple ? [...state.value.filter(v => v.id !== newItem.id), newItem] : [newItem]
            };
        }
        case actions.ADD_VALUE: {

            const newItem = action.payload.value;

            return {
                ...state,
                value: state.multiple ? [...state.value.filter(v => v.id !== newItem.id), newItem] : [newItem]
            };
        }
        case actions.HIGHLIGHT_FIRST_RESULT: {

            if (state.results.length === 0) {
                return state;
            }
            return {
                ...state,
                highlighted: state.results[0].id
            };
        }
        case actions.HIGHLIGHT_LAST_RESULT: {
            if (state.results.length === 0) {
                return state;
            }
            return {
                ...state,
                highlighted: state.results[state.results.length - 1].id
            };
        }
        case actions.HIGHLIGHT_NEXT_RESULT: {

            let pos = 0;

            if (state.results.length === 0) {
                return state;
            }

            const index = state.results.findIndex(result => result.id === state.highlighted);

            if (index !== -1) {
                pos = Math.min(index + 1, state.results.length - 1);
            }

            return {
                ...state,
                highlighted: state.results[pos].id
            };
        }
        case actions.HIGHLIGHT_PREV_RESULT: {
            let pos = 0;

            if (state.results.length === 0) {
                return state;
            }

            const index = state.results.findIndex(result => result.id === state.highlighted);

            if (index !== -1) {
                pos = Math.max(index - 1, 0);
            }

            return {
                ...state,
                highlighted: state.results[pos].id
            };
        }
        case actions.REMOVE_VALUE: {
            return {
                ...state,
                value: state.value.filter(v => v.id !== action.payload.id)
            };
        }
        case actions.RESET_SEARCH_VALUE: {
            return {
                ...state,
                searchValue: ''
            };
        }
        case actions.SELECT_HIGHLIGHTED_RESULT: {

            const result = state.results.find(r => r.id === state.highlighted);

            if (!result) {
                return state;
            }

            return {
                ...state,
                showResults: false,
                value: state.multiple ? [...state.value.filter(value => value.id !== result.id), result] : [result]
            };
        }
        case actions.SELECT_RESULT: {

            const result = state.results.find(r => r.id === action.payload.resultId);

            if (!result) {
                return state;
            }

            return {
                ...state,
                showResults: false,
                value: state.multiple ? [...state.value.filter(value => value.id !== result.id), result] : [result]
            };
        }
        case actions.SET_ALLOW_CUSTOM_VALUES: {
            return {
                ...state,
                allowCustomValues: action.payload.allowCustomValues
            };
        }
        case actions.SET_MULTIPLE: {
            return {
                ...state,
                multiple: action.payload.multiple
            };
        }
        case actions.SET_RESULTS: {
            return {
                ...state,
                results: action.payload.results,
                showResults: action.payload.results.length > 0,
                highlighted: action.payload.results.length > 0 ? action.payload.results[0].id : undefined
            };
        }
        case actions.SET_SEARCH_VALUE: {
            return {
                ...state,
                searchValue: action.payload.searchValue,
                showAllResults: false
            };
        }
        case actions.SET_SHOW_ALL_RESULTS: {
            return {
                ...state,
                showResults: action.payload.showAllResults,
                showAllResults: action.payload.showAllResults
            };
        }
        case actions.SET_SHOW_RESULTS: {
            return {
                ...state,
                showResults: action.payload.showResults
            };
        }
        case actions.SET_VALUE: {
            return {
                ...state,
                value: action.payload.value
            };
        }
        default: {
            return state;
        }
    }
}

export const getState = (state: any): State => state.searchAsYouType;

const getInstance = (instanceId: string): (state: any) => InstanceState => {
    return createSelector(getState, (state: State) => {
        return state.instances[instanceId] || initialInstanceState;
    });
};

export const getModel = (instanceId: string): (state: any) => SearchAsYouTypeModel => {
    return createSelector(getInstance(instanceId), (state: InstanceState): SearchAsYouTypeModel => {
        return {
            value: state.value,
            results: state.results,
            showResults: state.showResults,
            highlightedResult: state.results.find(result => result.id === state.highlighted)
        };
    });
};

export const getValue = (instanceId: string): (state: any) => Array<SearchResultModel> => {
    return createSelector(getInstance(instanceId), (state: InstanceState) => state.value || []);
};

export const getResults = (instanceId: string): (state: any) => Array<SearchResultModel> => {
    return createSelector(getInstance(instanceId), (state: InstanceState) => state.results);
};

export const getShowResults = (instanceId: string): (state: any) => boolean => {
    return createSelector(getInstance(instanceId), (state: InstanceState) => state.results.length > 0 && state.showResults);
};

export const getHighlightedResultId = (instanceId: string): (state: any) => string => {
    return createSelector(getInstance(instanceId), (state: InstanceState) => state.highlighted);
};



// WEBPACK FOOTER //
// ./src/app/atoms/search-as-you-type/search-as-you-type.reducer.ts